/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import { Grid, CircularProgress, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { UserActivitySummary } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { NUMBER_FORMATTER } from "../utils/utils";

interface UserPerformanceCardsProps {
  userActivitySummary: UserActivitySummary[];
  isLoading: boolean;
}

interface UserPerformanceCard {
  title: string;
  value: string;
  subtitle: string;
  color: string;
}

export const UserPerformanceCards: React.FC<UserPerformanceCardsProps> = ({
  userActivitySummary,
  isLoading,
}) => {
  const theme = useTheme();
  const generateUserCards = (): UserPerformanceCard[] => {
    if (!userActivitySummary || userActivitySummary.length === 0) {
      return [];
    }

    const sortedUsers = [...userActivitySummary].sort(
      (a, b) => b.total_changes - a.total_changes
    );

    const topUser = sortedUsers[0];
    const totalChanges = userActivitySummary.reduce(
      (sum, user) => sum + user.total_changes,
      0
    );
    const totalApproved = userActivitySummary.reduce(
      (sum, user) => sum + user.approved_changes,
      0
    );
    const averageApprovalRate = totalApproved / totalChanges;

    const topPerformer = [...userActivitySummary].sort(
      (a, b) =>
        b.approved_changes / b.total_changes -
        a.approved_changes / a.total_changes
    )[0];

    const mostActiveUser = [...userActivitySummary].sort((a, b) => {
      const aDuplicates = Number.isNaN(a.duplicates_processed)
        ? 0
        : a.duplicates_processed;
      const bDuplicates = Number.isNaN(b.duplicates_processed)
        ? 0
        : b.duplicates_processed;
      return bDuplicates - aDuplicates;
    })[0];

    return [
      {
        title: "Most Active User",
        value: topUser.user_name,
        subtitle: `${NUMBER_FORMATTER.format(topUser.total_changes)} changes`,
        color: theme.palette.primary.main,
      },
      {
        title: "Highest Approval %",
        value: topPerformer.user_name,
        subtitle: `${Math.round(
          (topPerformer.approved_changes / topPerformer.total_changes) * 100
        )}% approved`,
        color:
          topPerformer.approved_changes / topPerformer.total_changes >
          averageApprovalRate
            ? theme.palette.success.main
            : theme.palette.warning.main,
      },
      {
        title: "Duplicate Hunter",
        value: mostActiveUser.user_name,
        subtitle: `${NUMBER_FORMATTER.format(
          Number.isNaN(mostActiveUser.duplicates_processed)
            ? 0
            : mostActiveUser.duplicates_processed
        )} duplicates`,
        color: theme.palette.info.main,
      },
      {
        title: "Team Size",
        value: userActivitySummary.length.toString(),
        subtitle: "active users",
        color: theme.palette.secondary.main,
      },
      {
        title: "Overall Approval %",
        value: `${Math.round(averageApprovalRate * 100)}%`,
        subtitle: "team average",
        color:
          averageApprovalRate > 0.8
            ? theme.palette.success.main
            : averageApprovalRate > 0.6
            ? theme.palette.warning.main
            : theme.palette.error.main,
      },
    ];
  };

  const cards = generateUserCards();

  return (
    <>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid>
          <Typography variant="h4" gutterBottom>
            Team Highlights
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={6}>
              {cards.map((card) => (
                <Grid
                  key={card.title}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 2.4,
                  }}
                >
                  <MiraCard style={{ height: "100%" }}>
                    <MiraCardContent
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "140px",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Typography variant="h6" color="text.secondary">
                          {card.title}
                        </Typography>
                        <Typography variant="h4" component="span">
                          {card.icon}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        component="div"
                        color={card.color}
                        style={{ fontWeight: 600, marginBottom: 8, flex: 1 }}
                      >
                        {card.value}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        style={{ marginTop: "auto" }}
                      >
                        {card.subtitle}
                      </Typography>
                    </MiraCardContent>
                  </MiraCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};
